import { useState } from 'preact/hooks';
import type { UserPreferences } from '@voice-assistant/shared';
import { useModels, type EffortLevel, type RegistryModel } from '../hooks/useModels';
import { EffortRuler } from '../shared-ui/EffortRuler';
import { useSettingsStore } from '../state/settings.store';

/**
 * AI Tuning tab — Phase 3a of settings-zustand-migration.
 *
 * Previously took `draft: Partial<UserPreferences>` + `patch: (update) => void`
 * as props so the parent SettingsModal could batch changes through `boardDraft`
 * and `saveUserPreferences` on the Save button. Phase 3a swaps that for
 * direct store consumption — fields are read via `useSettingsStore` selectors
 * and written via `useSettingsStore.getState().patchMemo({...})` immediately
 * (the patchMemo action is optimistic locally and persists to the backend in
 * the same tick — see settings.store.ts).
 *
 * Behavior change: AI Tuning settings are now **live-applied** instead of
 * being held in the modal's local draft until the Save button is clicked.
 * That's the right UX for these settings (effort + token budgets affect the
 * NEXT message, not retroactively) — and it's the pattern Phase 3b will
 * extend to the rest of the modal.
 *
 * Plan: voice-assistant/plans/settings-zustand-migration.md
 * Tracking: #1125 (Phase 3a — this PR ticks AITuningTab; Phase 3b ticks the
 * SettingsModal main-body boardDraft refactor)
 */

const TOKEN_LIMIT_ROWS: Array<{
  key: keyof Pick<UserPreferences, 'akbContextBudget' | 'semanticContextBudget' | 'classifierMaxTokens' | 'outputTokenBudget'>;
  label: string;
  hint: string;
  defaultVal: number;
  min: number;
  max: number;
}> = [
  { key: 'akbContextBudget',      label: 'AKB DB context budget',      hint: 'Max tokens injected from knowledge base rows',       defaultVal: 4000, min: 100,  max: 32000 },
  { key: 'semanticContextBudget', label: 'Semantic context budget',     hint: 'Max tokens injected via semantic search per turn',    defaultVal: 600,  min: 100,  max: 8000  },
  { key: 'classifierMaxTokens',   label: 'Haiku classifier max tokens', hint: 'Max output tokens for the Haiku quick-task handler',  defaultVal: 300,  min: 50,   max: 2000  },
  { key: 'outputTokenBudget',     label: 'Output token budget',         hint: 'Advisory Claude output budget (0 = unlimited)',      defaultVal: 0,    min: 0,    max: 32000 },
];

interface Props {
  /** Capability gate for the 1M-context default toggle. Free tier hides it. */
  isPaidTier: boolean;
}

const isOpus = (m: RegistryModel) => /opus/i.test(m.modelId);
const isSonnet = (m: RegistryModel) => /sonnet/i.test(m.modelId);

/** Embeddings A/B toggle options (plans/embeddings-ab-toggle.md §3). Value is the raw
 *  `UserPreferences.embeddingsAbMode` — labels/copy are the natural-English surface the
 *  operator asked for ("Use DB embeddings to enrich context"), not the internal jargon. */
const EMBEDDINGS_AB_OPTIONS: Array<{ value: NonNullable<UserPreferences['embeddingsAbMode']>; label: string; hint: string }> = [
  { value: 'on', label: 'Always', hint: 'Every answer is enriched with your saved knowledge base.' },
  { value: 'off', label: 'Never', hint: 'Answers never pull from your saved knowledge base.' },
  {
    value: 'auto',
    label: 'Auto (recommended)',
    hint: 'Randomly turns it on or off for each message so we can measure whether it actually helps — you won’t be able to tell which one fired.',
  },
];

export function AITuningTab({ isPaidTier }: Props) {
  const { byProvider, loading } = useModels();
  // rawValues tracks the in-progress string while the user is typing.
  // Without this, controlled `value={displayVal}` resets the input on every re-render
  // triggered by validation errors — making it impossible to type e.g. "4100"
  // character by character because "4" fails min=100 → re-render → value snaps back to 4000.
  const [rawValues, setRawValues] = useState<Partial<Record<string, string>>>({});
  const [tokenErrors, setTokenErrors] = useState<Partial<Record<string, string>>>({});

  // Live field reads from the store. Defaults are applied by DEFAULT_USER_PREFERENCES
  // at store init; if the backend hasn't loaded yet, these are the defaults.
  const defaultOpusEffort = useSettingsStore((s) => s.defaultOpusEffort);
  const defaultSonnetEffort = useSettingsStore((s) => s.defaultSonnetEffort);
  const default1mContextEnabled = useSettingsStore((s) => s.default1mContextEnabled);
  const akbContextBudget = useSettingsStore((s) => s.akbContextBudget);
  const semanticContextBudget = useSettingsStore((s) => s.semanticContextBudget);
  const classifierMaxTokens = useSettingsStore((s) => s.classifierMaxTokens);
  const outputTokenBudget = useSettingsStore((s) => s.outputTokenBudget);
  const embeddingsAbMode = useSettingsStore((s) => s.embeddingsAbMode) ?? 'on';

  const tokenBudgetValues: Record<string, number | undefined> = {
    akbContextBudget,
    semanticContextBudget,
    classifierMaxTokens,
    outputTokenBudget,
  };

  if (loading) return <p class="settings-hint">Loading models…</p>;

  const effortModels = byProvider.anthropic.filter((m) => m.capabilities?.supportsEffort);
  const oneMModels = byProvider.anthropic.filter((m) => m.capabilities?.supports1MContext);

  if (effortModels.length === 0 && oneMModels.length === 0) {
    return <p class="settings-hint">No tunable models available on this deployment.</p>;
  }

  const opusDefault = defaultOpusEffort ?? 'high';
  const sonnetDefault = defaultSonnetEffort ?? 'high';

  return (
    <div class="ai-tuning">
      {effortModels.length > 0 && (
        <div class="ai-tuning-block">
          <h4 class="ai-tuning-subhead">Default reasoning effort</h4>
          <p class="settings-hint">Applied to new sessions. Per-session changes still override this.</p>
          {effortModels.map((m) => {
            const allowed = (m.capabilities?.supportedEffortLevels ?? []) as EffortLevel[];
            const value: EffortLevel = isOpus(m) ? opusDefault : sonnetDefault;
            const onChange = (next: EffortLevel) => {
              if (isOpus(m)) useSettingsStore.getState().patchMemo({ defaultOpusEffort: next });
              else if (isSonnet(m)) useSettingsStore.getState().patchMemo({ defaultSonnetEffort: next });
            };
            // Opus and Sonnet share defaults across versions — render only one ruler per family.
            if (isOpus(m) && m.modelId !== effortModels.find(isOpus)?.modelId) return null;
            if (isSonnet(m) && m.modelId !== effortModels.find(isSonnet)?.modelId) return null;
            const familyLabel = isOpus(m) ? 'Opus' : isSonnet(m) ? 'Sonnet' : m.displayName;
            return (
              <div key={`effort-${familyLabel}`} class="ai-tuning-row">
                <div class="ai-tuning-row-label">
                  <span class="ai-tuning-family">{familyLabel}</span>
                  <span class="ai-tuning-cost">cost ↑ as effort ↑</span>
                </div>
                <EffortRuler
                  value={value}
                  allowed={allowed}
                  onChange={onChange}
                  groupName={`default-effort-${familyLabel.toLowerCase()}`}
                />
              </div>
            );
          })}
        </div>
      )}

      {isPaidTier && oneMModels.length > 0 && (
        <div class="ai-tuning-block">
          <h4 class="ai-tuning-subhead">1M context window — default for new chats</h4>
          <p class="settings-hint">
            When on, new chats with {oneMModels.map((m) => m.displayName).join(', ')} start with 1M context enabled.
            You can toggle it per-chat in the context bar. Higher per-token billing tier applies.
          </p>
          <label class="ai-tuning-toggle">
            <input
              type="checkbox"
              checked={!!default1mContextEnabled}
              onChange={(e) =>
                useSettingsStore.getState().patchMemo({
                  default1mContextEnabled: (e.target as HTMLInputElement).checked,
                })
              }
            />
            <span>Enable 1M context by default for new chats</span>
          </label>
        </div>
      )}

      <div class="ai-tuning-block">
        <h4 class="ai-tuning-subhead">Use saved knowledge to enrich context</h4>
        <p class="settings-hint">
          Controls whether your saved knowledge base (semantic search + learned context) is used to
          enrich Claude's answers.
        </p>
        <div class="embeddings-ab-options" role="radiogroup" aria-label="Use saved knowledge to enrich context">
          {EMBEDDINGS_AB_OPTIONS.map(({ value, label, hint }) => (
            <label key={value} class="embeddings-ab-option">
              <input
                type="radio"
                name="embeddings-ab-mode"
                value={value}
                checked={embeddingsAbMode === value}
                onChange={() => useSettingsStore.getState().patchMemo({ embeddingsAbMode: value })}
              />
              <span class="embeddings-ab-option-text">
                <span class="embeddings-ab-option-label">{label}</span>
                <span class="embeddings-ab-option-hint">{hint}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div class="ai-tuning-block">
        <h4 class="ai-tuning-subhead">Token limits</h4>
        <p class="settings-hint">Fine-tune context and output budgets for A/B testing or cost control.</p>
        <table class="token-limits-table">
          <thead>
            <tr>
              <th>Setting</th>
              <th>Value</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {TOKEN_LIMIT_ROWS.map(({ key, label, hint, defaultVal, min, max }) => {
              const currentVal = tokenBudgetValues[key];
              const displayVal = currentVal ?? defaultVal;
              const err = tokenErrors[key];
              const isDefault = currentVal === undefined || currentVal === defaultVal;
              return (
                <tr key={key} class={err ? 'token-row-error' : ''}>
                  <td class="token-row-label">
                    <span class="token-row-name">{label}</span>
                    <span class="token-row-hint">{hint}</span>
                  </td>
                  <td class="token-row-input">
                    <input
                      type="number"
                      min={min}
                      max={max}
                      value={rawValues[key] ?? String(displayVal)}
                      onInput={(e) => {
                        const str = (e.target as HTMLInputElement).value;
                        setRawValues(prev => ({ ...prev, [key]: str }));
                        if (tokenErrors[key]) {
                          setTokenErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
                        }
                      }}
                      onBlur={(e) => {
                        const str = (e.target as HTMLInputElement).value;
                        const parsed = parseInt(str, 10);
                        if (isNaN(parsed) || parsed < min || parsed > max) {
                          setTokenErrors(prev => ({ ...prev, [key]: `Must be ${min}–${max}` }));
                          setRawValues(prev => { const n = { ...prev }; delete n[key]; return n; });
                        } else {
                          setTokenErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
                          setRawValues(prev => { const n = { ...prev }; delete n[key]; return n; });
                          useSettingsStore.getState().patchMemo({ [key]: parsed });
                        }
                      }}
                    />
                    {err && <span class="token-row-err-msg">{err}</span>}
                  </td>
                  <td class="token-row-reset">
                    {!isDefault && (
                      <button
                        class="token-reset-btn"
                        title="Reset to default"
                        onClick={() => {
                          useSettingsStore.getState().patchMemo({ [key]: defaultVal });
                          setTokenErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
                          setRawValues(prev => { const n = { ...prev }; delete n[key]; return n; });
                        }}
                      >↺</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
